import CopyableId from "@/components/common/CopyableId";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { MultiSelect } from "@/components/common/MultiSelect";
import { SearchableSelect } from "@/components/common/SearchableSelect";
import { api, formatApiError } from "@/lib/api";
import { VISIT_TYPES } from "@/pages/fleet/constants";

const EMPTY_VISIT = { patient_id: "", visit_type: "", priority: "routine", scheduled_at: "", notes: "", clinician_ids: [], form_ids: [], required_medical_specialty: "", required_sub_specialty: "", required_specialty_type: "" };

export default function CreateVisitModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_VISIT);
  const [patientQuery, setPatientQuery] = useState("");
  const [patientResults, setPatientResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [assignable, setAssignable] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [publishedForms, setPublishedForms] = useState([]);
  const [lookups, setLookups] = useState({ medical: [], sub: [], level: [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) { setForm(EMPTY_VISIT); setPatientQuery(""); setPatientResults([]); setSelectedPatient(null); setAssignable([]); return; }
    api.get("/forms", { params: { status: "published" } })
      .then((r) => setPublishedForms((r.data.items || []).map((f) => ({ value: f.id, label: f.name }))))
      .catch(() => {});
    // Specialty lookups for the required-specialty fields.
    (async () => {
      try {
        const cats = await api.get("/lookups/categories", { params: { page_size: 200 } });
        const byCode = {};
        (cats.data.items || []).forEach((c) => { byCode[c.code] = c.id; });
        const fetchVals = async (code) => {
          if (!byCode[code]) return [];
          const r = await api.get("/lookups/values", { params: { category_id: byCode[code], status: "active", page_size: 300 } });
          return r.data.items || [];
        };
        const [medical, sub, level] = await Promise.all([
          fetchVals("medical_specialization"), fetchVals("sub_specialty"), fetchVals("specialty_type"),
        ]);
        setLookups({ medical, sub, level });
      } catch { /* non-blocking */ }
    })();
  }, [open]);

  // (Re)load assignable staff with availability whenever the modal opens or the time changes
  useEffect(() => {
    if (!open) return;
    setLoadingStaff(true);
    const params = {};
    if (form.scheduled_at) params.at = new Date(form.scheduled_at).toISOString();
    api.get("/fleet/assignable", { params })
      .then((r) => {
        const items = r.data.items || [];
        setAssignable(items);
        // Drop any selected staff who became busy at the new time
        const availableIds = new Set(items.filter((c) => !c.busy).map((c) => c.clinician_id));
        setForm((f) => ({ ...f, clinician_ids: f.clinician_ids.filter((id) => availableIds.has(id)) }));
      })
      .catch(() => setAssignable([]))
      .finally(() => setLoadingStaff(false));
  }, [open, form.scheduled_at]);

  useEffect(() => {
    if (!open || patientQuery.trim().length < 1 || selectedPatient) { setPatientResults([]); return; }
    const t = setTimeout(() => {
      api.get("/fleet/patients/search", { params: { q: patientQuery.trim() } })
        .then((r) => setPatientResults(r.data.items || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [patientQuery, open, selectedPatient]);

  function pickPatient(p) {
    setSelectedPatient(p);
    set("patient_id", p.id);
    setPatientResults([]);
    setPatientQuery(`${p.name} (${p.mrn})`);
  }

  async function submit() {
    if (!form.patient_id) return toast.error("Select a patient");
    if (!form.visit_type) return toast.error("Select a visit type");
    setSaving(true);
    try {
      const payload = {
        patient_id: form.patient_id,
        source_module: "manual",
        visit_type: form.visit_type,
        priority: form.priority,
        notes: form.notes || null,
        form_ids: form.form_ids || [],
      };
      if (form.required_medical_specialty) payload.required_medical_specialty = form.required_medical_specialty;
      if (form.required_sub_specialty) payload.required_sub_specialty = form.required_sub_specialty;
      if (form.required_specialty_type) payload.required_specialty_type = form.required_specialty_type;
      if (form.scheduled_at) payload.scheduled_at = new Date(form.scheduled_at).toISOString();
      const r = await api.post("/fleet/visits", payload);
      const visit = r.data;
      if (form.clinician_ids.length > 0 && visit?.id) {
        try {
          await api.post(`/fleet/visits/${visit.id}/assign`, { clinician_ids: form.clinician_ids });
          toast.success(`Visit created and assigned to ${form.clinician_ids.length} staff`);
        } catch (e) {
          toast.warning(`Visit created, but assignment failed: ${formatApiError(e)}`);
        }
      } else {
        toast.success("Visit created");
      }
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="create-visit-modal">
        <DialogHeader><DialogTitle>Create Visit</DialogTitle>
          <DialogDescription>Dispatch a new home visit for a patient. Leave the clinician unassigned to use AI Assign later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <FormField label="Patient" required>
            <div className="relative">
              <Input value={patientQuery}
                onChange={(e) => { setPatientQuery(e.target.value); setSelectedPatient(null); set("patient_id", ""); }}
                placeholder="Search by name, MRN or phone…" data-testid="create-visit-patient-search" />
              {patientResults.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-56 overflow-auto">
                  {patientResults.map((p) => (
                    <button key={p.id} type="button" onClick={() => pickPatient(p)}
                      className="w-full text-left px-3 py-2 hover:bg-[#F5F7FA] text-sm border-b border-[#F1F5F9] last:border-0"
                      data-testid={`create-visit-patient-option-${p.id}`}>
                      <div className="font-medium text-[#1F2937]">{p.name}</div>
                      <div className="text-xs text-[#6B7280] font-mono"><CopyableId value={p.mrn} kind="MRN" />{p.phone ? ` · ${p.phone}` : ""}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </FormField>

          <FormField label="Visit type" required>
            <Select value={form.visit_type} onValueChange={(v) => set("visit_type", v)}>
              <SelectTrigger data-testid="create-visit-type"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {Object.entries(VISIT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>

          <FormField label="Priority">
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="create-visit-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="routine">Routine</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          <div className="rounded-lg border border-[#E5E7EB] p-3 space-y-3 bg-[#F8FAFC]">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">
              Required specialty (for AI matching)
            </div>
            <p className="text-[11px] text-[#94A3B8] -mt-1">
              Leave blank to auto-fill from the patient's care team. Used to rank clinicians by specialty fit.
            </p>
            <FormField label="Medical specialty">
              <SearchableSelect
                testid="create-visit-medical-specialty"
                options={lookups.medical.map((o) => ({ value: o.value, label: o.label }))}
                value={form.required_medical_specialty}
                onChange={(v) => setForm((f) => ({ ...f, required_medical_specialty: v, required_sub_specialty: "" }))}
                placeholder="Any / auto from care team"
              />
            </FormField>
            <FormField label="Sub-specialty">
              <SearchableSelect
                testid="create-visit-sub-specialty"
                options={lookups.sub
                  .filter((o) => !form.required_medical_specialty || o.parent_value === form.required_medical_specialty)
                  .map((o) => ({ value: o.value, label: o.label }))}
                value={form.required_sub_specialty}
                onChange={(v) => set("required_sub_specialty", v)}
                placeholder={form.required_medical_specialty ? "Any sub-specialty" : "Pick a medical specialty first"}
                disabled={!form.required_medical_specialty}
              />
            </FormField>
            <FormField label="Specialty level">
              <SearchableSelect
                testid="create-visit-specialty-level"
                options={lookups.level.map((o) => ({ value: o.value, label: o.label }))}
                value={form.required_specialty_type}
                onChange={(v) => set("required_specialty_type", v)}
                placeholder="Any level"
              />
            </FormField>
          </div>

          <FormField label="Scheduled time (optional)">
            <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)}
              data-testid="create-visit-scheduled" />
          </FormField>

          <FormField label="Forms to fill during visit (optional)">
            <MultiSelect testid="create-visit-forms" options={publishedForms} values={form.form_ids}
              onChange={(v) => set("form_ids", v)} placeholder="Select forms for the visiting team…" showPrimaryBadge={false} />
          </FormField>

          <FormField label="Assign staff (optional)">
            <MultiSelect
              testid="create-visit-clinicians"
              options={assignable.filter((c) => !c.busy).map((c) => ({
                value: c.clinician_id,
                label: `${c.name}${c.role_code ? ` · ${c.role_code.replace(/_/g, " ")}` : ""}`,
              }))}
              values={form.clinician_ids}
              onChange={(v) => set("clinician_ids", v)}
              placeholder={loadingStaff ? "Checking availability…" : "Select available staff…"}
              showPrimaryBadge={false}
            />
            {(() => {
              const busyCount = assignable.filter((c) => c.busy).length;
              if (busyCount > 0 && form.scheduled_at) {
                return <p className="text-xs text-[#B45309] mt-1" data-testid="create-visit-busy-hint">{busyCount} staff hidden — they have a visit within 60 min of this time.</p>;
              }
              if (!form.scheduled_at) {
                return <p className="text-xs text-[#6B7280] mt-1">Set a scheduled time to filter by availability.</p>;
              }
              return null;
            })()}
          </FormField>

          <FormField label="Notes (optional)">
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
              placeholder="Clinical context or instructions…" data-testid="create-visit-notes" />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="create-visit-submit">
            {saving ? "Creating…" : "Create Visit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
